/**
 * Icon Bundle — Pre-registers Material Symbols icons for offline use (PWA)
 *
 * @version 2.1.0
 * @description Imports specific icons from @iconify-json/material-symbols and
 *              registers them with @iconify/vue's local storage. This ensures
 *              these icons are available instantly and offline — no API call needed.
 *
 * HOW IT WORKS:
 * - @iconify-json/material-symbols contains ALL icons as a JSON file (~8MB, dev only)
 * - We extract only the icons we need using Iconify's `getIconData()` utility
 * - `addIcon()` registers each icon in @iconify/vue's in-memory cache
 * - At runtime, the Icon component finds them locally → no network request
 *
 * WHAT TO PRE-REGISTER:
 * 1. Vuetify internal aliases (mandatory — framework won't render without them)
 * 2. Icons used in components via prepend-icon, append-icon, icon props
 * 3. Icons used in <v-icon> throughout the app
 *
 * NAMING CONVENTION:
 * - The `material-symbols` collection uses the OUTLINED style as default
 * - Some icons have an explicit `-outline` suffix, others don't
 * - All names below are verified against @iconify-json/material-symbols icons.json
 *
 * @see https://iconify.design/docs/icon-components/vue/offline.html
 */

import { addIcon } from '@iconify/vue'
import { icons as materialSymbolsData } from '@iconify-json/material-symbols'

// =============================================================================
// IMPORTANT: Offline-only icon loading
// =============================================================================
// @iconify/vue fetches missing icons from api.simplesvg.com by default.
// For PWA offline-first, ALL icons must be pre-registered below.
// If an icon is missing here, it won't render and a console.warn fires.
// Run the app in dev and check for "[icon-bundle] Icon not found" warnings.
// =============================================================================

// =============================================================================
// Utility — Extract & Register
// =============================================================================

/**
 * Registers a single icon from the Material Symbols collection.
 * Falls back silently if the icon doesn't exist in the collection.
 */
/**
 * Risolve un nome seguendo gli ALIAS della collezione (es.
 * `local-fire-department-outline` → `mode-heat-outline`). Prima gli alias
 * venivano ignorati: l'icona risultava "non trovata", non veniva registrata
 * e a runtime partiva una fetch verso api.iconify.design (offline: niente).
 */
function resolveIconData(name: string): { body: string; width?: number; height?: number } | null {
  const collection = materialSymbolsData as unknown as {
    icons: Record<string, { body: string; width?: number; height?: number }>
    aliases?: Record<string, { parent: string }>
  }
  let current = name
  for (let hops = 0; hops < 8; hops++) {
    const icon = collection.icons[current]
    if (icon) return icon
    const alias = collection.aliases?.[current]
    if (!alias) return null
    current = alias.parent
  }
  return null
}

function registerIconAs(name: string, source: string): void {
  const iconData = resolveIconData(source)
  if (iconData) {
    addIcon(`material-symbols:${name}`, {
      body: iconData.body,
      width: iconData.width ?? materialSymbolsData.width ?? 24,
      height: iconData.height ?? materialSymbolsData.height ?? 24,
    })
  } else {
    console.warn(`[icon-bundle] Icon "material-symbols:${source}" not found in collection`)
  }
}

/**
 * Registers a single icon from the Material Symbols collection (alias inclusi).
 * Falls back silently if the icon doesn't exist in the collection.
 */
function registerIcon(name: string): void {
  registerIconAs(name, name)
}

/**
 * Registers multiple icons at once.
 */
function registerIcons(names: string[]): void {
  names.forEach(registerIcon)
}

/**
 * Registra `name` con il disegno di `source`: per nomi che NON esistono nella
 * collezione ma sono già in giro (in anagrafica settori sul DB, in codice
 * vecchio). Il plugin `iconify-subset` di vite.config.ts legge anche queste
 * chiamate. Es. `checkroom-outline` (seed di `sectors.icon` per Maglieria):
 * Material Symbols ha solo `checkroom`.
 */
function registerIconAlias(name: string, source: string): void {
  registerIconAs(name, source)
}

// =============================================================================
// 1. Vuetify Internal Aliases (MANDATORY)
// =============================================================================
// These are the icons used by Vuetify's own components (VCheckbox, VSelect, etc.)
// Must match the values in msAliases from materialSymbols.ts

registerIcons([
  // Navigation & Actions
  'keyboard-arrow-up',
  'check',
  'cancel',
  'close',
  'chevron-left',
  'chevron-right',
  'keyboard-arrow-down',
  'menu',
  'arrow-drop-down',

  // Feedback / Status
  'check-circle',
  'info',
  'warning',
  'error',

  // Checkboxes
  'check-box',
  'check-box-outline-blank',
  'indeterminate-check-box',

  // Radio
  'radio-button-checked',
  'radio-button-unchecked',

  // Sorting
  'arrow-upward',
  'arrow-downward',

  // Pagination
  'first-page',
  'last-page',

  // Carousel
  'circle',

  // Ratings
  'star-outline',
  'star',
  'star-half',

  // Misc
  'progress-activity',
  'unfold-more',
  'attach-file',
  'add',
  'remove',
  'edit',
  'calendar-today',
  'colorize',

  // Treeview
  'arrow-right',

  // Color picker
  'palette',

  // Keyboard hints (VKbd / shortcut display)
  'keyboard-command-key',
  'keyboard-control-key',
  'space-bar',
  'shift',
  'keyboard-option-key',
  'keyboard-return',
  'arrow-forward',
  'backspace',
])

// =============================================================================
// 2. App-Specific Icons
// =============================================================================
// All icons used in .vue files, verified against the collection.
// Organized by functional area for maintainability.
//
// MDI → Material Symbols mapping reference:
//   See /admin/src/plugins/iconsets/ICON_MAP.md

registerIcons([
  // ── Global / Shell ─────────────────────────────────────────────────────
  'check-circle-outline',     // mdi-check-circle → snackbar success
  'error-outline',            // mdi-alert-circle, mdi-alert-circle-outline → snackbar error
  'info-outline',             // mdi-information, mdi-information-outline → snackbar info, alerts
  'wifi-off',                 // mdi-wifi-off → offline indicator
  'warning-outline',          // mdi-alert, mdi-alert-outline → warnings, v-alert
  'help-outline',             // mdi-help-circle → confirm dialog
  'refresh',                  // mdi-refresh → toolbar refresh
  'logout',                   // mdi-logout → user menu

  // ── Navigation ─────────────────────────────────────────────────────────
  'arrow-back',               // mdi-arrow-left → back buttons
  'add-circle-outline',       // mdi-plus-circle, mdi-plus-circle-outline → FAB / add buttons
  'open-in-new',              // mdi-open-in-new → external links

  // ── CRUD Actions ───────────────────────────────────────────────────────
  'edit-outline',             // mdi-pencil → edit buttons
  'delete-outline',           // mdi-delete, mdi-delete-outline → delete buttons
  'save-outline',             // mdi-content-save → save buttons
  'content-copy-outline',     // mdi-content-copy → duplicate
  'search',                   // mdi-magnify → search fields
  'download',                 // mdi-download → download buttons
  'upload',                   // mdi-upload → upload buttons
  'undo',                     // mdi-undo → undo/reset

  // ── People & Communication ─────────────────────────────────────────────
  'person-outline',           // mdi-account → user fields
  'badge',                    // mdi-account-tie → agent badge in activity cards
  'group-outline',            // mdi-account-group → toolbar user count
  'call-outline',             // mdi-phone → phone links
  'phone-in-talk-outline',    // mdi-phone-in-talk → agent phone stats
  'mail-outline',             // mdi-email, mdi-email-outline → email links
  'mark-email-read-outline',  // mdi-email-check → password reset
  'forward-to-inbox-outline', // mdi-email-fast → agent email stats
  'sms-outline',              // mdi-message-text → activity type SMS
  'chat-outline',             // mdi-whatsapp → WhatsApp fallback (brand icon N/A)
  'forum-outline',            // mdi-comment-question-outline → ask user dialog

  // ── Calendar & Time ────────────────────────────────────────────────────
  'calendar-today-outline',   // mdi-calendar → date pickers, dialogs
  'calendar-clock-outline',   // mdi-calendar-clock → datetime picker
  'date-range-outline',       // mdi-calendar-range → date range picker
  'event-available-outline',  // mdi-calendar-check → activity dialog
  'edit-calendar-outline',    // mdi-clock-edit-outline → working hours dialog
  'schedule-outline',         // mdi-clock-outline, mdi-clock-time-four-outline → time fields
  'lock-outline',             // mdi-lock → password fields

  // ── Documents & Files ──────────────────────────────────────────────────
  'picture-as-pdf-outline',   // mdi-file-pdf-box → PDF buttons
  'table-chart-outline',      // mdi-file-excel, mdi-file-excel-outline → Excel export
  'edit-document-outline',    // mdi-file-document-edit-outline → quote edit
  'task-outline',             // mdi-file-check-outline → close intervention
  'folder-open-outline',      // mdi-folder-open-outline → sectors
  'print-outline',            // mdi-printer → print buttons
  'print-disabled-outline',   // mdi-printer-off → printer unavailable
  'ios-share',                // PdfViewerDialog → Condividi (FE v2.174.0)
  'zoom-in',                  // PdfViewerDialog → Ingrandisci
  'zoom-out',                 // PdfViewerDialog → Riduci

  // ── Images & Media ─────────────────────────────────────────────────────
  'add-photo-alternate-outline', // mdi-image-plus → upload image dialog
  'backup-outline',           // mdi-cloud-upload → drag-drop upload zone
  'hide-image-outline',       // mdi-image-remove → remove image
  'image-outline',            // mdi-image-edit-outline → select image dialog
  'crop',                     // mdi-crop → crop tool
  'pinch-zoom-in',            // mdi-gesture-pinch → zoom hint
  'draw-outline',             // mdi-draw → signature

  // ── Location & Transport ───────────────────────────────────────────────
  'location-on-outline',      // mdi-map-marker → address, visits
  'directions-car-outline',   // mdi-car → technician car dialog
  'commute-outline',          // mdi-car-clock → intervention transfers

  // ── Tech / Machines ────────────────────────────────────────────────────
  'settings-outline',         // mdi-cog-outline → machine operations
  'inventory-2-outline',      // mdi-package-variant-closed → materials dialog
  'notifications-active-outline', // mdi-bell-ring → alert dialog

  // ── Misc UI ────────────────────────────────────────────────────────────
  'do-not-disturb-on-outline', // mdi-minus-circle-outline → cart quantity decrement (MS name for ⊖)
  'drag-handle',              // mdi-menu (drag context) → sortable list handles
  'picture-as-pdf',           // mdi-file-pdf-box → quote PDF link (filled variant)
  'title',                    // mdi-format-title → activity title field
  'notes',                    // mdi-note-text → activity notes field
  'short-text',               // mdi-text → alert text field
  'emergency-outline',        // mdi-asterisk → other activities stats
  'settings-backup-restore',  // mdi-backup-restore → price restore

  // ── Mobile Quick Access Bar (Fase 1 Responsive) ────────────────────────
  'manage-accounts-outline',    // admin: utenti
  'assignment-turned-in-outline', // admin: report attività agenti
  'price-check',                // commercial: preventivi
  'assignment-outline',         // tech: rapportini
  'warehouse-outline',          // tech: materiali
  'contacts-outline',           // tech: clienti
  'light-mode',                 // theme toggle: light (no outline variant)
  'dark-mode',                  // theme toggle: dark (no outline variant)
  'filter-list',                // mobile filter bar: trigger icon (non esiste la -outline)
  'close-small-outline',        // mobile filter chips: clear single chip
  'filter-list-off',            // mobile filter bar / monitor "Tutti" (non esiste la -outline)
  'print-outline',               // intervention card: CST print icon
  'description-outline',         // intervention card: fattura CST icon
  'settings-backup-restore',     // mobile filter bar: clear/reset filters
  'sort',                         // mobile filter dialog: sort section icon

  // ── Agenda & Calendar ──────────────────────────────────────────────────
  'event-busy',                   // agenda: empty day state icon
  'phone-in-talk',                // agenda: activity count (filled variant)
  'notifications-active',         // agenda: alert count (filled variant)

  // ── Filled variants used alongside outline ones ────────────────────────
  'real-estate-agent',            // interventions: agent icon
  'signpost',                     // customer details: DDM icon
  'person',                       // various: person filled
  'visibility',                   // various: show/hide toggle
  'visibility-off',               // various: show/hide toggle
  'content-copy',                 // various: copy to clipboard (filled)
  'history',                      // interventions: storia button
  'send',                         // various: send email
  'link',                         // various: external link
  'tune',                         // various: filter/settings

  // ── InterventionsDetails + detail pages ─────────────────────────────
  'domain',                       // customer company icon
  'map-outline',                  // map link icon
  'directions-outline',           // DDM directions icon
  'cancel-outline',               // invia a cliente = false
  'precision-manufacturing',      // macchina section + inline
  'id-card-outline',              // chi & dove section icon
  'verified-user',                // warranty valid icon
  'event',                        // warranty date icon
  'build-outline',                // warranty MO icon
  'gpp-bad-outline',              // warranty not available icon
  'sticky-note-2-outline',        // notes button icon
  'local-fire-department-outline', // urgency display icon
  'handyman-outline',             // tecnico icon
  'home-repair-service-outline',  // CST icon
  'person-add',                   // CST assign placeholder
  'folder-managed-outline',       // gestione section icon
  'euro',                         // tipo fattura/garanzia/contratto
  'calendar-month-outline',       // data chiusura icon
  'draw',                         // firma cliente (filled variant)

  // ── Area Icons (HORECA/SELF/MAGLIERIA) ─────────────────────────
  'room-service-outline',         // HORECA area icon
  'local-laundry-service-outline', // SELF area icon
  'checkroom',                    // MAGLIERIA area icon (l'anagrafica dice `checkroom-outline`, vedi alias sotto)

  // ── Prospect Questionnaire (BE v1.38.0–1.38.2 / FE v2.66.11) ─────
  // Renders the 6 HubSpot qualification questions stored in
  // `prospects.extra_fields` (JSON column). See ProspectDetails.vue
  // section-questionnaire card and mail/prospects/new-prospect-assigned.blade.
  'quiz-outline',                 // questionnaire card header + nav menu
  'work-outline',                 // situazione_professionale_attuale
  'lightbulb-outline',            // come_mai_vorresti_aprire_una_lavanderia_selfservice
  'store-outline',                // hai_gia_individuato_un_locale
  'payments-outline',             // quanto_sei_disposto_a_investire
  // (location-on-outline + schedule-outline già registrati sopra,
  //  riusati per `se_si_in_quale_citta` e
  //  `con_quali_tempistiche_vorresti_avviare_il_tuo_progetto`)

  // ── Provenienza prospect ↔ cliente (BE v1.142.0 / FE v2.150.0) ───
  // Legame `metodo_clienti.app_prospect_id`, scritto solo dal gestionale:
  // card "Provenienza" in CustomerDetails e "Convertito in cliente" in
  // ProspectDetails.
  'how-to-reg-outline',           // conversione prospect → cliente
  'arrow-right-alt',              // freccia prospect → cliente
  'smartphone-outline',           // cellulare (era gia' usata in ProspectDetails
                                  // senza essere registrata → console.warn + icona
                                  // assente offline)

  // ── R2 Commesse — Piano di consegna ──────────────────────────────
  'place-item',                   // link "Imposta …" che porta la data
                                  // proposta nel campo (CommessaDetails.vue,
                                  // card Piano di consegna)

  // ── R2 Commesse — Data simulata (collaudo super-admin) ───────────
  'update',                       // bottone/chip "Data simulata" (SimulatedDateControl.vue)
  'experiment-outline',           // banner modalità attiva

  // ── R2 Commesse — Campanella notifiche ──────────────────────────
  'notifications-outline',        // NotificationsBell.vue (toolbar desktop + drawer mobile)

  // ── R2 Commesse — card Commessa a righe (CommessaDetails.vue) ────
  'tag',                          // codice commessa
  'category-outline',             // settore tecnico
  'request-quote-outline',        // preventivo di origine

  // ── Foto rapportini (FE v2.148.0) ────────────────────────────────
  'photo-camera-outline',         // card «Foto» + nav menu (InterventionPhotosCard.vue)
  'add-a-photo-outline',          // dropzone / pulsante mobile "Scatta o scegli foto"
  'replay',                       // retry upload fallito
  'drag-indicator',               // hint riordino griglia
])

// =============================================================================
// Alias per nomi NON esistenti nella collezione ma già in uso (DB / codice)
// =============================================================================
// `checkroom-outline` è il valore seminato in `sectors.icon` per Maglieria
// (migration 2026_08_20_120000): Material Symbols ha solo `checkroom`.
// Gli altri tre erano nomi sbagliati nel codice, ora corretti; restano come
// alias per eventuali usi residui.
registerIconAlias('checkroom-outline', 'checkroom')
registerIconAlias('filter-list-outline', 'filter-list')
registerIconAlias('filter-list-off-outline', 'filter-list-off')
registerIconAlias('sort-outline', 'sort')
